/*
 * Copyright (c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { appConstants } from '../data-model/app-constants';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { BaseService } from './base.service';
import { AuditRecord_Exam, ProxyRequest } from '../data-model/proxy.types';
import { ExamInfo } from '../data-model/exam-data';

const log = new Logger('ExamContextService');

@Injectable({
  providedIn: 'root'
})
export class ExamContextService implements OnDestroy {
  // Local Property
  private examData = {};
  private examServiceDetail;
  private serviceDetailsSubscription: Subscription;
  // Observer
  private examDataObserver: BehaviorSubject<any> = new BehaviorSubject(this.examData);
  getExamData(): Observable<any> {
    return this.examDataObserver.asObservable();
  }

  setExamData(data) {
    this.examData = data;
    this.examDataObserver.next(data);
  }

  // Constructor
  constructor(protected dataService: ProxyDataService, private baseService: BaseService) {}

  // Service initialize
  initialize(examId: number): void {
    log.debug('RM Exam Service - initialize invoked', examId);
    // Load Service Map from proxy and exam specific details
    this.serviceDetailsSubscription = this.baseService.getServiceDetails().subscribe(details => {
      this.examServiceDetail = { ...details?.exam };
      this.fetchExamContext(this.examServiceDetail?.get, examId);
    });
  }

  ngOnDestroy(): void {
    this.serviceDetailsSubscription && this.serviceDetailsSubscription.unsubscribe();
  }

  // Base function to load the exam data
  fetchExamContext(request, examId: number) {
    log.debug('RM Exam Service - Fetch invoked', request, examId);
    if (request && examId) {
      const payload = { ...request };
      payload.configValue = payload.configValue.replace('{id}', examId);
      log.debug('RM Exam Service - Fetch start', payload);

      this.baseService
        .loadData(appConstants.appURLs.proxyFetch, payload)
        .then(data => {
          this.setExamData(data);
          log.debug('RM Exam Service - Fetch complete', this.examData);
        })
        .catch(ex => {
          this.setExamData(ex);
          log.debug('RM Exam Service - Fetch failed', ex);
        });
    }
  }

  fetchExamStatus(examId: number): Promise<any> {
    log.debug('RM Exam Service - Fetch Status invoked', examId);
    return this.baseService.loadData(appConstants.appURLs.clinicalInfo.replace('{id}', examId.toString()));
  }

  updateExam(editExamPayload: ExamInfo, auditRecord: AuditRecord_Exam): Promise<any> {
    log.debug('RM Exam Service - Fetch invoked', editExamPayload.internalPatientId);
    const payloadData: ProxyRequest = this.examServiceDetail?.edit;
    payloadData.payload = editExamPayload;
    payloadData.auditRecord = auditRecord;
    return this.baseService.loadData(appConstants.appURLs.proxyFetch, payloadData);
  }
}
